import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar,
  Plus,
  Phone,
  Video,
  MapPin,
  Clock,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [formData, setFormData] = useState({
    contact_id: "",
    appointment_type: "phone",
    scheduled_date: "",
    scheduled_time: "10:00",
    duration_minutes: 30,
    notes: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [appointmentsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/appointments${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`),
        axios.get(`${API}/contacts`)
      ]);
      setAppointments(Array.isArray(appointmentsRes.data) ? appointmentsRes.data : []);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
    } catch (error) {
      toast.error("Fehler beim Laden der Termine");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, formData);
      toast.success("Termin erstellt");
      setShowDialog(false);
      setFormData({
        contact_id: "",
        appointment_type: "phone",
        scheduled_date: "",
        scheduled_time: "10:00",
        duration_minutes: 30,
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const formData = new FormData();
      formData.append("status", status);
      await axios.put(`${API}/appointments/${id}`, formData);
      toast.success("Status aktualisiert");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("Termin gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "phone":
        return <Phone className="w-4 h-4 text-blue-600" />;
      case "video":
        return <Video className="w-4 h-4 text-purple-600" />;
      case "in_person":
        return <MapPin className="w-4 h-4 text-emerald-600" />;
      default:
        return <Calendar className="w-4 h-4 text-slate-500" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      phone: "Telefon",
      video: "Video",
      in_person: "Vor-Ort"
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      scheduled: { label: "Geplant", class: "bg-blue-50 text-blue-700 border-blue-200", icon: Clock },
      completed: { label: "Abgeschlossen", class: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
      cancelled: { label: "Abgesagt", class: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
      no_show: { label: "Nicht erschienen", class: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle }
    };
    const s = statusMap[status] || statusMap.scheduled;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.class}`}>
        <s.icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  // Group appointments by date
  const groupedAppointments = appointments.reduce((groups, apt) => {
    const date = apt.scheduled_date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(apt);
    return groups;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="appointments-title">
            Termine
          </h1>
          <p className="text-slate-500 mt-1">{appointments.length} Termine insgesamt</p>
        </div>
        <div className="flex gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="scheduled">Geplant</SelectItem>
              <SelectItem value="completed">Abgeschlossen</SelectItem>
              <SelectItem value="cancelled">Abgesagt</SelectItem>
              <SelectItem value="no_show">Nicht erschienen</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setShowDialog(true)}
            className="bg-slate-900 hover:bg-slate-800 btn-press"
            data-testid="create-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Termin
          </Button>
        </div>
      </div>

      {/* Appointments List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-slate-200 animate-pulse rounded-xl"></div>
          ))}
        </div>
      ) : appointments.length === 0 ? (
        <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
          <CardContent className="py-16">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Keine Termine</h3>
              <p className="text-slate-500 mt-2">
                Termine werden automatisch aus Gesprächen erstellt oder können manuell hinzugefügt werden
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedAppointments).sort().map(([date, dateAppointments]) => (
            <div key={date}>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {formatDate(date)}
              </h2>
              <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] overflow-hidden">
                <div className="divide-y divide-slate-100">
                  {dateAppointments.map((apt) => (
                    <div 
                      key={apt.id}
                      className="p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`appointment-${apt.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                            {getTypeIcon(apt.appointment_type)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{apt.contact_name}</span>
                              <span className="text-sm text-slate-500">• {getTypeLabel(apt.appointment_type)}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {apt.scheduled_time} Uhr
                              </span>
                              <span>• {apt.duration_minutes} Min.</span>
                              {apt.contact_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3.5 h-3.5" />
                                  {apt.contact_phone}
                                </span>
                              )}
                            </div>
                            {apt.notes && (
                              <p className="text-sm text-slate-500 mt-1 line-clamp-1">{apt.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {apt.google_event_id && (
                            <span className="text-xs text-emerald-600 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              Kalender
                            </span>
                          )}
                          {getStatusBadge(apt.status)}
                          <Select 
                            value={apt.status} 
                            onValueChange={(value) => handleStatusChange(apt.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="scheduled">Geplant</SelectItem>
                              <SelectItem value="completed">Abgeschlossen</SelectItem>
                              <SelectItem value="cancelled">Abgesagt</SelectItem>
                              <SelectItem value="no_show">Nicht erschienen</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(apt.id)}
                            className="h-8 w-8 text-slate-500 hover:text-red-600"
                            data-testid={`delete-appointment-${apt.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Create Appointment Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="appointment-dialog">
          <DialogHeader>
            <DialogTitle className="">Neuer Termin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact">Kontakt *</Label>
              <Select 
                value={formData.contact_id} 
                onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
              >
                <SelectTrigger data-testid="contact-select">
                  <SelectValue placeholder="Kontakt auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.name} - {contact.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Art des Termins</Label>
              <Select 
                value={formData.appointment_type} 
                onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
              >
                <SelectTrigger data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Telefontermin</SelectItem>
                  <SelectItem value="video">Videotermin</SelectItem>
                  <SelectItem value="in_person">Vor-Ort-Termin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Datum *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                  data-testid="date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Uhrzeit *</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  required
                  data-testid="time-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Dauer (Minuten)</Label>
              <Select 
                value={String(formData.duration_minutes)} 
                onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 Minuten</SelectItem>
                  <SelectItem value="30">30 Minuten</SelectItem>
                  <SelectItem value="45">45 Minuten</SelectItem>
                  <SelectItem value="60">60 Minuten</SelectItem>
                  <SelectItem value="90">90 Minuten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={3}
                data-testid="notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!formData.contact_id || !formData.scheduled_date}
                data-testid="save-appointment-btn"
              >
                Termin erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
